import { memo, useMemo } from 'react'
import { formatBalance } from '../lib/balance'
import { safeOpenLink, safeSheetOpenHaptic } from '../lib/safeTelegram'
import { getWebApp } from '../lib/telegram'
import ProfileAvatar from './ProfileAvatar'
import { Button, Label, MiniLabel } from './figma'
import { cashbackLevelName, cashbackProgressPercent } from '../lib/cashback'

function openTelegramLink(url) {
  const link = String(url ?? '').trim()
  if (!link) return

  safeSheetOpenHaptic()

  const tg = window.Telegram?.WebApp
  try {
    if (typeof tg?.openTelegramLink === 'function') {
      tg.openTelegramLink(link)
      return
    }
  } catch (error) {
    console.error('[ProfilePage] openTelegramLink failed', error)
  }

  safeOpenLink(link)
}

function FigmaDirectionIcon({ className = '' }) {
  return (
    <img loading="eager"
      src="/images/icons/figma/direction-right.svg"
      alt=""
      aria-hidden="true"
      width="24"
      height="24"
      className={`h-6 w-6 shrink-0 object-contain opacity-50 ${className}`}
    />
  )
}

function FigmaIcon({ src, size = 24, className = '' }) {
  return (
    <img loading="eager"
      src={src}
      alt=""
      width={size}
      height={size}
      className={`${size === 20 ? 'h-5 w-5' : 'h-6 w-6'} shrink-0 object-contain ${className}`}
      aria-hidden="true"
    />
  )
}

function BalanceActionButton({ label, icon, primary = false, onClick, className = '' }) {
  return (
    <Button
      type={primary ? 'main' : 'neutral'}
      size="action"
      onClick={onClick}
      className={`${primary
        ? '!border-white/50 !bg-white !text-[var(--figma-accent)]'
        : '!border-white/20 !bg-white/20 !text-white'} ${className}`}
    >
      <FigmaIcon src={icon} size={20} />
      <span className="font-[510] tracking-[-0.01em]">{label}</span>
    </Button>
  )
}

function ProfileMenuRow({ label, icon, onClick, divider = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[56px] items-center gap-3 px-4 text-left transition-transform active:scale-[0.99] ${divider ? 'border-b border-white/10' : ''}`}
    >
      <FigmaIcon src={icon} />
      <span className="min-w-0 flex-1 text-[16px] font-[510] leading-[22px] tracking-[-0.01em] text-white">{label}</span>
      <FigmaDirectionIcon />
    </button>
  )
}

function ProfilePage({ user, profile, loading, error, onTopUp, onOpenAbout, onOpenHistory, onOpenPromo }) {
  const data = user || profile

  const progressPercent = useMemo(() => cashbackProgressPercent(data), [data])

  const handleTopUp = () => {
    safeSheetOpenHaptic()
    onTopUp?.()
  }

  const handleOpenAbout = () => {
    safeSheetOpenHaptic()
    onOpenAbout?.()
  }

  const handleOpenHistory = () => {
    safeSheetOpenHaptic()
    onOpenHistory?.()
  }

  const supportLink = data?.support_link || ''
  const handleOpenSupport = () => {
    if (!supportLink) return
    safeSheetOpenHaptic()
    openTelegramLink(supportLink)
  }

  const handleOpenPromo = () => {
    safeSheetOpenHaptic()
    onOpenPromo?.()
  }

  if (loading) {
    return (
      <div className="space-y-4 pt-2">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 animate-pulse rounded-full bg-[var(--color-border)]" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 animate-pulse rounded bg-[var(--color-border)]" />
            <div className="h-3 w-24 animate-pulse rounded bg-[var(--color-card-elevated)]" />
          </div>
        </div>
        <div className="h-44 animate-pulse rounded-2xl bg-[var(--color-border)]" />
        <div className="grid grid-cols-2 gap-2.5">
          <div className="h-20 animate-pulse rounded-xl bg-[var(--color-card-elevated)]" />
          <div className="h-20 animate-pulse rounded-xl bg-[var(--color-card-elevated)]" />
        </div>
        <div className="premium-card h-36 animate-pulse rounded-2xl" />
        <div className="premium-card h-14 animate-pulse rounded-2xl" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="premium-card rounded-2xl px-4 py-5 text-center text-sm text-red-500">
        {error}
      </div>
    )
  }

  const tgUser = getWebApp().initDataUnsafe?.user ?? {}
  const photoUrl = tgUser.photo_url || ''
  const firstName = tgUser.first_name || data?.username || 'Пользователь'
  const telegramId = tgUser.id ?? data?.user_id
  const displayUsername = data?.username || tgUser.username

  const totalBalance = data?.balance ?? 0
  const promoBalance = data?.promo_balance ?? 0
  const levelName = cashbackLevelName(data)
  const cashbackPercent = data?.cashback_percent ?? 0
  const turnover = data?.turnover ?? 0
  const nextLevelRub = data?.next_level_rub ?? 0

  return (
    <div className="flex flex-col gap-6 pt-2">
      <header className="flex h-16 items-center gap-3">
        <ProfileAvatar photoUrl={photoUrl} name={firstName} size={64} />
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="truncate text-[24px] font-[590] leading-[28px] tracking-[-0.01em] text-white">{firstName}</p>
          {displayUsername ? (
            <p className="truncate text-[12px] font-[510] leading-[14px] tracking-[-0.01em] text-white/68">@{displayUsername}</p>
          ) : null}
          {telegramId ? (
            <p className="text-[12px] font-[510] leading-[14px] tracking-[-0.01em] text-white/50">ID: {telegramId}</p>
          ) : null}
        </div>
      </header>

      <section className="home-figma-card home-figma-hero flex h-[158px] flex-col justify-between rounded-2xl p-2">
        <div className="flex items-start justify-between gap-3 p-2">
          <div className="flex flex-col gap-1">
            <p className="text-[14px] font-[510] leading-[16px] tracking-[-0.01em] text-white/68">Ваш баланс</p>
            <p className="text-[28px] font-[590] leading-[32px] tracking-[-0.01em] text-white">
              {formatBalance(totalBalance)} ₽
            </p>
          </div>
          {promoBalance > 0 ? (
            <Label type="contrast" className="bg-white/20 text-white">
              Бонусы: {formatBalance(promoBalance)} ₽
            </Label>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-1">
          <BalanceActionButton
            label="Пополнить"
            icon="/images/icons/figma/profile-plus.svg"
            primary
            onClick={handleTopUp}
          />
          <BalanceActionButton
            label="Промокод"
            icon="/images/icons/figma/promo.svg"
            onClick={handleOpenPromo}
          />
        </div>
      </section>

      <section className="home-figma-card relative flex h-[174px] flex-col overflow-hidden rounded-2xl p-4">
        <div className="relative z-[2] flex items-center justify-between">
          <p className="text-[20px] font-[590] leading-[24px] tracking-[-0.01em] text-white">{levelName}</p>
          <Label type="contrast" className="bg-white/10 text-white">Кэшбэк {cashbackPercent}%</Label>
        </div>

        <div className="relative z-[2] mt-auto space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[14px] font-[510] leading-4 tracking-[-0.01em] text-white/68">Общий оборот</span>
            <span className="text-[16px] font-[590] leading-5 tracking-[-0.01em] text-white">{formatBalance(turnover)} ₽</span>
          </div>
          <div className="h-[6px] overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[#FF9B36] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex min-h-[18px] items-center justify-end">
            {nextLevelRub > 0 ? (
              <span className="truncate text-right text-[12px] font-[510] leading-[14px] tracking-[-0.01em] text-white/68">
                До уровня {formatBalance(nextLevelRub)} ₽
              </span>
            ) : (
              <MiniLabel type="orange">MAX LVL</MiniLabel>
            )}
          </div>
        </div>
      </section>

      <section className={`home-figma-card flex flex-col overflow-hidden rounded-2xl ${supportLink ? 'h-[170px]' : 'h-[114px]'}`}>
        <ProfileMenuRow
          label="История покупок"
          icon="/images/icons/figma/history.svg"
          onClick={handleOpenHistory}
        />
        {supportLink ? (
          <ProfileMenuRow
            label="Поддержка"
            icon="/images/icons/figma/support.svg"
            onClick={handleOpenSupport}
          />
        ) : null}
        <ProfileMenuRow
          label="Информация"
          icon="/images/icons/figma/info.svg"
          onClick={handleOpenAbout}
          divider={false}
        />
      </section>
    </div>
  )
}

export default memo(ProfilePage)
